import { useRef, useState, useEffect } from "react";
import { ChartCard } from "./chart-card";
import { ProgressList, type SortMode } from "./progress-list";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
} from "recharts";
import { Download } from "lucide-react";
import {
	type StatisticsByHighSchool,
	type PaginatedResponse,
	type StatisticsFilter,
} from "@/lib/statistics-api.service";
import statisticsAPIService from "@/lib/statistics-api.service";
import { useAuth } from "@/global-hooks/useAuth";
import { PrioritySchoolComparison } from "./priority-school-comparison";
import { COLORS, downloadChartPng, toAPIFilter } from "./utils";
import {
	BAR_VALUE_LABEL,
	NO_ANIMATION,
	CATEGORY_AXIS_EXTRA_HEIGHT,
	CATEGORY_AXIS_HEIGHT,
	WrappedCategoryTick,
	chartMinWidth,
} from "./chart-ticks";
import {
	StatisticsFilterControls,
	PaginationControls,
	type StatisticsFilterType,
	type StatisticsFilters,
} from "./StatisticsControls";

interface HighSchoolsTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

export default function HighSchoolsTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
}: HighSchoolsTabProps = {}) {
	const highSchoolRef = useRef<HTMLDivElement>(null);
	const { user } = useAuth();

	// State for filtering and pagination
	const [filterType, setFilterType] = useState<StatisticsFilterType>("total");
	const [chartLimit, setChartLimit] = useState(20);
	const [itemsPerPage, setItemsPerPage] = useState(24);
	const [currentPage, setCurrentPage] = useState(1);
	const [highSchoolData, setHighSchoolData] =
		useState<PaginatedResponse<StatisticsByHighSchool> | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [sortBy, setSortBy] = useState<SortMode>("count");

	// Fetch data with filters
	const fetchHighSchoolData = async (
		type: StatisticsFilterType,
		limit?: number,
		offset?: number,
	) => {
		setIsLoading(true);
		try {
			const filter: StatisticsFilter = toAPIFilter(filters, type);
			if (limit !== undefined) filter.limit = limit;
			if (offset !== undefined) filter.offset = offset;
			filter.sortBy = sortBy;

			const data = await statisticsAPIService.getStatsByHighSchool(filter);
			setHighSchoolData(data);
		} catch (error) {
			console.error("Error fetching high school data:", error);
			// Set empty data on error
			setHighSchoolData({
				data: [],
				total: 0,
				page: 1,
				limit: limit || 50,
				hasNext: false,
				hasPrev: false,
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Effect to fetch data when filter changes
	useEffect(() => {
		const offset = (currentPage - 1) * itemsPerPage;
		fetchHighSchoolData(filterType, itemsPerPage, offset);
		// `filters` belongs in here: the khoa dropdown and the date range live on
		// this tab too, and without it the controls change while the table does not.
		// `refreshToken` cũng vậy: nút Tải lại của trang phải làm mới cả bảng này,
		// không riêng phần số liệu do trang tự giữ.
		// `sortBy` nằm ở server: nó quyết định trang này gồm những trường nào, chứ
		// không chỉ đảo thứ tự vài dòng đang hiện.
	}, [filterType, currentPage, itemsPerPage, filters, refreshToken, sortBy]);

	// Handle filter change
	const handleFilterChange = (type: StatisticsFilterType) => {
		setFilterType(type);
		setCurrentPage(1); // Reset to first page
	};

	// Đổi thứ tự là đổi cả danh sách, nên trang 3 của thứ tự cũ không còn nghĩa gì
	// với thứ tự mới — về trang đầu, giống như khi đổi bộ lọc.
	const handleSortByChange = (next: SortMode) => {
		setSortBy(next);
		setCurrentPage(1);
	};

	// Handle pagination
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	// Get chart data based on filter type
	const getChartData = () => {
		if (!highSchoolData?.data) return [];

		return highSchoolData.data.slice(0, chartLimit).map((school) => {
			const chartEntry: any = {
				schoolName: school.schoolName,
				total: school.total,
			};

			switch (filterType) {
				case "enrollment_status":
					chartEntry.received = school.received;
					chartEntry.pending = school.pending;
					break;
				case "payment_status":
					chartEntry.paid = school.paid;
					chartEntry.unpaid = school.unpaid;
					break;
				case "paid_only":
					chartEntry.paid = school.paid;
					break;
				case "unpaid_only":
					chartEntry.unpaid = school.unpaid;
					break;
				case "received_only":
					chartEntry.received = school.received;
					break;
				default: // total
					chartEntry.received = school.received;
					chartEntry.pending = school.pending;
					break;
			}

			return chartEntry;
		});
	};

	// Get bars for chart based on filter type
	const getChartBars = () => {
		switch (filterType) {
			case "enrollment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[2]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[3]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "payment_status":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "paid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="paid"
						dataKey="paid"
						fill={COLORS[4]}
						name="Đã đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "unpaid_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="unpaid"
						dataKey="unpaid"
						fill={COLORS[5]}
						name="Chưa đóng học phí"
						label={BAR_VALUE_LABEL}
					/>,
				];
			case "received_only":
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[2]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
			default: // total
				return [
					<Bar
						{...NO_ANIMATION}
						key="received"
						dataKey="received"
						fill={COLORS[0]}
						name="Đã tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
					<Bar
						{...NO_ANIMATION}
						key="pending"
						dataKey="pending"
						fill={COLORS[1]}
						name="Chưa tiếp nhận"
						label={BAR_VALUE_LABEL}
					/>,
				];
		}
	};

	const totalPages = highSchoolData ? Math.ceil(highSchoolData.total / itemsPerPage) : 1;

	if (isLoading && !highSchoolData) {
		return (
			<TabsContent value="high-schools">
				<div className="flex items-center justify-center h-64">
					<p>Đang tải dữ liệu...</p>
				</div>
			</TabsContent>
		);
	}

	return (
		<TabsContent value="high-schools">
			<div ref={highSchoolRef} className="space-y-6">
				<div className="flex justify-between items-center">
					<h2 className="text-2xl font-semibold">Thống kê theo trường THPT</h2>
					<div className="flex items-center gap-2">
						{(user?.role === "admin" || user?.role === "manager") && (
							<Button
								onClick={async () => {
									try {
										await statisticsAPIService.exportHighSchoolStatsAsCSV(
											toAPIFilter(filters, filterType),
										);
									} catch (error) {
										console.error("Error exporting CSV:", error);
										alert("Không thể tải CSV. Vui lòng thử lại.");
									}
								}}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
						)}
						<Button
							onClick={() => downloadChartPng(highSchoolRef, "thong-ke-theo-truong-thpt")}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Download className="h-4 w-4" />
							Tải PNG
						</Button>
					</div>
				</div>

				{/* Đặt trên bảng xếp hạng trường: câu "nguồn tuyển đến từ nhóm ưu tiên
				    bao nhiêu phần" đọc trước thì phần còn lại của tab mới có ngữ cảnh. */}
				<PrioritySchoolComparison filters={filters} refreshToken={refreshToken} />

				<StatisticsFilterControls
					filterType={filterType}
					onFilterChange={handleFilterChange}
					chartLimit={chartLimit}
					onChartLimitChange={setChartLimit}
					filters={filters}
					onFiltersChange={onFiltersChange}
					showFilters={true}
				/>

				<ChartCard
					title="Thống kê theo trường THPT"
					filename="thong-ke-theo-truong-thpt"
					description={`Hiển thị top ${chartLimit} trường có nhiều sinh viên nhất`}
				>
					<div className="overflow-x-auto">
						<div style={{ minWidth: chartMinWidth(getChartData().length) }}>
							<ResponsiveContainer width="100%" height={500 + CATEGORY_AXIS_EXTRA_HEIGHT}>
								<BarChart
									data={getChartData()}
									margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
								>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis
										dataKey="schoolName"
										interval={0}
										tick={<WrappedCategoryTick />}
										height={CATEGORY_AXIS_HEIGHT}
									/>
									<YAxis />
									<Tooltip />
									<Legend />
									{getChartBars()}
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>
				</ChartCard>

				<ProgressList
					title="Tiến độ theo trường THPT"
					description={`${highSchoolData?.data.length ?? 0} trường trong trang này`}
					remainderLabel="Chưa nộp hồ sơ, chưa đóng phí"
					sortBy={sortBy}
					onSortByChange={handleSortByChange}
					isRefreshing={isLoading}
					rows={(highSchoolData?.data ?? []).map((school, index) => ({
						id: `${school.schoolName}-${index}`,
						label: school.schoolName,
						sublabel: school.provinceName,
						total: school.total,
						primary: school.received,
						segments: [
							{
								key: "received",
								label: "Đã tiếp nhận",
								value: school.received,
								className: "bg-green-600",
							},
							{
								key: "paidButNotReceived",
								label: "Đã đóng phí, chưa tiếp nhận",
								value: school.paidButNotReceived,
								className: "bg-green-300 dark:bg-green-800",
							},
						],
						details: [
							{ label: "Tổng", value: school.total },
							{
								label: "Đã tiếp nhận",
								value: school.received,
								className: "text-green-700 dark:text-green-400",
							},
							{
								label: "Chưa tiếp nhận",
								value: school.pending,
								className: "text-orange-700 dark:text-orange-400",
							},
							{
								label: "Đã đóng học phí",
								value: school.paid,
								className: "text-blue-600 dark:text-blue-400",
							},
							{
								label: "Chưa đóng học phí",
								value: school.unpaid,
								className: "text-red-600 dark:text-red-400",
							},
							{
								label: "Đã đóng phí chưa tiếp nhận",
								value: school.paidButNotReceived,
								className: "text-orange-700 dark:text-orange-400",
							},
							{
								label: "Chưa nộp, chưa đóng phí",
								value: school.notReceivedAndNotPaid,
								className: "text-muted-foreground",
							},
						],
					}))}
				/>

				<PaginationControls
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					hasNext={highSchoolData?.hasNext || false}
					hasPrev={highSchoolData?.hasPrev || false}
					itemsPerPage={itemsPerPage}
					onItemsPerPageChange={setItemsPerPage}
				/>
			</div>
		</TabsContent>
	);
}
